<template>
  <v-dialog v-model="open" max-width="1480" persistent>
    <v-card class="wage-diag-dialog">
      <div class="wage-diag-header">
        <div class="wage-diag-header__icon">
          <v-icon size="18" color="white">mdi-file-chart</v-icon>
        </div>
        <div class="wage-diag-header__text">
          <h3 class="wage-diag-header__title">임금진단</h3>
          <p v-if="ccCode" class="wage-diag-header__sub">{{ name }} · {{ ccCode }}</p>
        </div>
        <v-spacer />
        <v-btn variant="text" class="btn-close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="wage-diag-body">
        <LoadingOverlay
          :show="loading || deleting"
          :text="deleting ? '삭제 중...' : loadingText"
          :variant="deleting ? 'delete' : 'default'"
        />

        <div class="wage-diag-form">
          <v-text-field
            v-model="ccCode"
            label="시설코드"
            density="compact"
            variant="outlined"
            hide-details
            readonly
            class="wage-diag-field wage-diag-field--code"
          />
          <v-text-field
            v-model="name"
            label="시설명"
            density="compact"
            variant="outlined"
            hide-details
            class="wage-diag-field"
          />
          <v-text-field
            v-model="biz"
            label="사업구분"
            density="compact"
            variant="outlined"
            hide-details
            class="wage-diag-field"
          />
          <input
            v-model="ym"
            type="month"
            class="wage-diag-month"
            min="2020-01"
            max="2050-12"
          />
          <label class="wage-diag-check">
            <input
              v-model="nonTaxLunch"
              type="checkbox"
              :disabled="loading || deleting"
              @change="onNonTaxLunchChange"
            />
            <span>비과세 중식대 적용</span>
          </label>
        </div>

        <div class="wage-diag-actions">
          <v-btn variant="flat" class="btn-reset" @click="downloadTemplate">
            <v-icon size="16" class="mr-1">mdi-download</v-icon>
            엑셀 양식
          </v-btn>
          <v-btn variant="flat" class="btn-register" @click="pickFile">
            <v-icon size="16" class="mr-1">mdi-file-excel</v-icon>
            엑셀 업로드
          </v-btn>
          <v-btn
            v-if="hasSaved"
            variant="flat"
            class="btn-delete"
            :disabled="loading || deleting"
            @click="showDeleteConfirm = true"
          >
            <v-icon size="16" class="mr-1">mdi-delete-outline</v-icon>
            진단 삭제
          </v-btn>
          <input
            ref="fileRef"
            type="file"
            accept=".xlsx,.xls"
            class="wage-diag-file-hidden"
            @change="onFileChange"
          />
          <span v-if="fileName" class="wage-diag-file-name">{{ fileName }} ({{ rowCount }}건)</span>
        </div>

        <v-alert v-if="error" type="error" density="compact" class="mt-3">
          {{ error }}
        </v-alert>

        <div class="wage-diag-preview">
          <div class="wage-diag-preview__bar">
            <div class="wage-diag-preview__tabs">
              <button
                type="button"
                class="wage-diag-preview__tab"
                :class="{ 'wage-diag-preview__tab--active': reportVersion === 'v1' }"
                @click="reportVersion = 'v1'"
              >
                버전1
              </button>
              <button
                type="button"
                class="wage-diag-preview__tab"
                :class="{ 'wage-diag-preview__tab--active': reportVersion === 'v2' }"
                @click="reportVersion = 'v2'"
              >
                버전2
              </button>
            </div>
            <div class="wage-diag-preview__title-wrap">
              <span class="wage-diag-preview__label">
                {{ reportVersion === 'v1' ? '임금대장 개선 결과보고서' : '임금대장 무료 진단 보고서' }}
              </span>
              <span v-if="activePdfFileName" class="wage-diag-preview__filename">
                {{ activePdfFileName }}
              </span>
            </div>
            <div class="wage-diag-preview__actions">
              <v-menu
                v-if="reportVersion === 'v2' && isSuperAdmin"
                location="bottom end"
                :close-on-content-click="false"
                max-width="440"
              >
                <template #activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    variant="text"
                    icon
                    size="small"
                    class="wage-diag-formula-help-btn"
                    title="통상임금 리스크 계산식 (최고관리자)"
                  >
                    <v-icon size="18" color="#546e7a">mdi-help-circle-outline</v-icon>
                  </v-btn>
                </template>
                <v-card class="wage-diag-formula-card">
                  <div class="wage-diag-formula-card__title">통상임금 리스크 계산식 (버전2)</div>
                  <div class="wage-diag-formula-card__badge">최고관리자 전용</div>
                  <div class="wage-diag-formula-card__section">
                    <div class="wage-diag-formula-card__label">판결 이전 통상임금 (BEFORE)</div>
                    <div class="wage-diag-formula-card__formula">= 기본급 (업로드 원본)</div>
                  </div>
                  <div class="wage-diag-formula-card__section">
                    <div class="wage-diag-formula-card__label">판결 이후 통상임금 (AFTER)</div>
                    <div class="wage-diag-formula-card__formula">= 기본급 + 중식대(▲) + 직책수당(▲)</div>
                    <ul class="wage-diag-formula-card__notes">
                      <li>중식대(▲): 비과세 중식대 처리 후 실제 지급액 (예: 20만)</li>
                      <li>직책수당(▲): 엑셀 직책수당 컬럼</li>
                      <li>기타수당: 통상임금·비과세와 별도 과세 수당</li>
                      <li>업로드 원본 중식대(예: 6만)와 처리 후 금액이 다를 수 있음</li>
                    </ul>
                  </div>
                  <div class="wage-diag-formula-card__section">
                    <div class="wage-diag-formula-card__label">통상시급 상승률</div>
                    <div class="wage-diag-formula-card__formula">= AFTER ÷ BEFORE</div>
                  </div>
                  <div class="wage-diag-formula-card__section">
                    <div class="wage-diag-formula-card__label">월 리스크 (RISK A, 1인)</div>
                    <div class="wage-diag-formula-card__formula">= (연장 + 야간 + 휴일) × (상승률 − 1)</div>
                  </div>
                  <div class="wage-diag-formula-card__section">
                    <div class="wage-diag-formula-card__label">3년 누적 리스크 (1인)</div>
                    <div class="wage-diag-formula-card__formula">= 월 리스크 × 36개월</div>
                  </div>
                  <div class="wage-diag-formula-card__example">
                    예) 기본 200만, 중식 20만, 직책 10만 → +15% → 연야 35만 × 15% = 52,500원/월
                  </div>
                </v-card>
              </v-menu>
              <v-btn
                variant="flat"
                class="btn-save"
                size="small"
                :disabled="!activePdfBlob"
                @click="downloadPdf"
              >
                <v-icon size="16" class="mr-1">mdi-download</v-icon>
                PDF 다운로드
              </v-btn>
            </div>
          </div>
          <div class="wage-diag-preview__body">
            <iframe
              v-if="activePdfUrl"
              :src="activePdfUrl"
              :title="activePdfFileName || '임금간편진단 보고서'"
              class="wage-diag-preview__frame"
            />
            <div v-else-if="hasReportPreview" class="wage-diag-preview__empty">
              <v-icon size="40" color="#90a4ae">mdi-file-pdf-box</v-icon>
              <p>{{ reportVersion === 'v1' ? '버전1' : '버전2' }} 보고서를 불러오지 못했습니다.</p>
            </div>
            <div v-else class="wage-diag-preview__empty">
              <v-icon size="40" color="#90a4ae">mdi-file-pdf-box</v-icon>
              <p>엑셀 업로드 후 보고서가 미리보기로 표시됩니다.</p>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <DeleteConfirmDialog
    v-model="showDeleteConfirm"
    title="임금진단 삭제"
    :target-name="name || ccCode"
    message="의 임금진단 데이터를 삭제하시겠습니까?"
    :loading="deleting"
    @confirm="confirmDelete"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import * as XLSX from 'xlsx';
import { crawlApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog.vue';

interface WageRow {
  name: string;
  worker: string;
  time?: number;
  base?: number;
  overtime?: number;
  night?: number;
  holiday?: number;
  longTerm?: number;
  sunim?: number;
  dang?: number;
  position?: number;
  etc?: number;
  lunch?: number;
  car?: number;
  childcare?: number;
  l_yeon?: number;
  gun?: number;
  jang?: number;
  go?: number;
  boss?: boolean;
}

interface FacilityTarget {
  ccNumber: string;
  name: string;
  biz?: string;
  wageDiagCount?: number;
  wageDiagWdate?: string;
  wageDiagUId?: string;
}

const props = defineProps<{
  modelValue: boolean;
  facility?: FacilityTarget | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  saved: [ccNumber: string];
  deleted: [ccNumber: string];
}>();

const store = useStore();

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

/** 최고관리자(SUPER_ADMIN, role_level 100) 전용 */
const isSuperAdmin = computed(
  () => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 100,
);

const loading = ref(false);
const deleting = ref(false);
const showDeleteConfirm = ref(false);
const loadingText = ref('처리 중...');
const error = ref('');
const ccCode = ref('');
const name = ref('');
const biz = ref('');
const ym = ref('');
const nonTaxLunch = ref(true);
const rows = ref<WageRow[]>([]);
const fileName = ref('');
const fileRef = ref<HTMLInputElement | null>(null);
const pdfUrl = ref<string | null>(null);
const pdfBlob = ref<Blob | null>(null);
const pdfFileName = ref('');
const pdfUrlV2 = ref<string | null>(null);
const pdfBlobV2 = ref<Blob | null>(null);
const pdfFileNameV2 = ref('');
const reportVersion = ref<'v1' | 'v2'>('v1');
const savedInfo = ref<FacilityTarget | null>(null);

const rowCount = computed(() => rows.value.length);
const hasSaved = computed(() => (savedInfo.value?.wageDiagCount || 0) > 0);
const hasReportPreview = computed(() => !!pdfUrl.value || !!pdfUrlV2.value);
const activePdfUrl = computed(() => (reportVersion.value === 'v1' ? pdfUrl.value : pdfUrlV2.value));
const activePdfBlob = computed(() => (reportVersion.value === 'v1' ? pdfBlob.value : pdfBlobV2.value));
const activePdfFileName = computed(() => (
  reportVersion.value === 'v1' ? pdfFileName.value : pdfFileNameV2.value
));

const num = (v: unknown) => {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const sanitizeFilePart = (value: string) => value
  .replace(/[\\/:*?"<>|]/g, '_')
  .replace(/\s+/g, '')
  .trim();

const buildPdfFileName = () => {
  const facility = sanitizeFilePart(name.value) || '시설';
  const code = sanitizeFilePart(ccCode.value) || '코드없음';
  const month = ym.value || new Date().toISOString().slice(0, 7);
  return `임금대장_개선결과보고서_${facility}_${code}_${month}.pdf`;
};

const buildPdfFileNameV2 = () => {
  const facility = sanitizeFilePart(name.value) || '시설';
  const code = sanitizeFilePart(ccCode.value) || '코드없음';
  const month = ym.value || new Date().toISOString().slice(0, 7);
  return `임금대장_무료진단보고서_${facility}_${code}_${month}.pdf`;
};

const initYm = () => {
  const now = new Date();
  ym.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const revokePdfUrl = (url: string | null) => {
  if (!url) return;
  const base = url.split('#')[0];
  window.URL.revokeObjectURL(base);
};

const resetPdf = () => {
  revokePdfUrl(pdfUrl.value);
  revokePdfUrl(pdfUrlV2.value);
  pdfUrl.value = null;
  pdfBlob.value = null;
  pdfFileName.value = '';
  pdfUrlV2.value = null;
  pdfBlobV2.value = null;
  pdfFileNameV2.value = '';
};

const applyPdfBase64 = (base64: string, fileName: string | undefined, version: 'v1' | 'v2') => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const resolvedName = fileName || (version === 'v1' ? buildPdfFileName() : buildPdfFileNameV2());
  const url = window.URL.createObjectURL(blob);
  const pdfSrc = `${url}#filename=${encodeURIComponent(resolvedName)}`;

  if (version === 'v1') {
    revokePdfUrl(pdfUrl.value);
    pdfBlob.value = blob;
    pdfFileName.value = resolvedName;
    pdfUrl.value = pdfSrc;
    return;
  }

  revokePdfUrl(pdfUrlV2.value);
  pdfBlobV2.value = blob;
  pdfFileNameV2.value = resolvedName;
  pdfUrlV2.value = pdfSrc;
};

const loadReportV2Pdf = async () => {
  if (!ccCode.value) return;

  try {
    const res = await crawlApi.getWageDiagnosisReportV2(ccCode.value);
    const base64 = res.data?.pdf;
    if (!base64) {
      throw new Error(res.data?.message || '보고서(v2) 데이터가 없습니다.');
    }
    applyPdfBase64(base64, res.data?.fileName, 'v2');
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `보고서(v2) 미리보기 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('보고서 v2 PDF 조회 오류:', e);
  }
};

const initFromFacility = async () => {
  const f = props.facility;
  if (!f) return;

  ccCode.value = f.ccNumber || '';
  name.value = f.name || '';
  biz.value = f.biz || '';
  savedInfo.value = {
    ccNumber: f.ccNumber,
    name: f.name,
    biz: f.biz,
    wageDiagCount: f.wageDiagCount,
    wageDiagWdate: f.wageDiagWdate,
    wageDiagUId: f.wageDiagUId,
  };

  if (f.wageDiagCount && f.wageDiagCount > 0) {
    try {
      const res = await crawlApi.getWageDiagnosisActive(f.ccNumber);
      const meta = res.data?.data?.meta;
      if (meta?.cwd_ym) ym.value = meta.cwd_ym;
      if (meta?.cwd_non_tax_lunch != null) {
        nonTaxLunch.value = meta.cwd_non_tax_lunch === '1';
      }
    } catch (e) {
      console.error('임금진단 메타 조회 오류:', e);
    }

    await loadSavedReport();
  }
};

const close = () => {
  open.value = false;
};

const pickFile = () => {
  if (!ccCode.value) {
    error.value = '시설코드가 없습니다.';
    return;
  }
  fileRef.value?.click();
};

const parseExcel = (data: ArrayBuffer) => {
  const wb = XLSX.read(data, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(sheet, {
    header: [
      'no', 'name', 'worker', 'time', 'base', 'overtime', 'night', 'holiday',
      'longTerm', 'dang', 'position', 'etc', 'lunch', 'car', 'childcare',
      'l_yeon', 'gun', 'jang', 'go', 'boss',
    ],
    range: 2,
  }) as Record<string, unknown>[];

  return json
    .filter((r) => r.name && r.worker)
    .map((r) => ({
      name: String(r.name || ''),
      worker: String(r.worker || '기타'),
      time: num(r.time),
      base: num(r.base),
      overtime: num(r.overtime),
      night: num(r.night),
      holiday: num(r.holiday),
      longTerm: num(r.longTerm),
      sunim: 0,
      dang: num(r.dang),
      position: num(r.position),
      etc: num(r.etc),
      lunch: num(r.lunch),
      car: num(r.car),
      childcare: num(r.childcare),
      l_yeon: num(r.l_yeon),
      gun: num(r.gun),
      jang: num(r.jang),
      go: num(r.go),
      boss: !!r.boss,
    }));
};

const uploadAndReport = async () => {
  error.value = '';

  if (!ccCode.value.trim()) {
    error.value = '시설코드가 필요합니다.';
    return;
  }
  if (!name.value.trim()) {
    error.value = '시설명을 입력해주세요.';
    return;
  }
  if (!ym.value) {
    error.value = '연월을 선택해주세요.';
    return;
  }
  if (rows.value.length === 0) {
    error.value = '엑셀 파일을 업로드해주세요.';
    return;
  }

  loading.value = true;
  loadingText.value = '저장 및 보고서 생성 중...';
  resetPdf();

  try {
    const res = await crawlApi.uploadWageDiagnosis({
      ccNumber: ccCode.value.trim(),
      name: name.value.trim(),
      biz: biz.value.trim(),
      ym: ym.value,
      nonTaxLunch: nonTaxLunch.value,
      rows: rows.value,
    });

    const base64 = res.data?.pdf;
    if (!base64) {
      throw new Error(res.data?.message || '보고서 데이터가 없습니다.');
    }

    applyPdfBase64(base64, res.data?.fileName, 'v1');
    await loadReportV2Pdf();
    savedInfo.value = {
      ccNumber: ccCode.value,
      name: name.value,
      biz: biz.value,
      wageDiagCount: res.data?.saved || rows.value.length,
      wageDiagWdate: new Date().toISOString(),
      wageDiagUId: res.data?.uploadId,
    };
    emit('saved', ccCode.value);
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `임금진단 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('임금진단 업로드 오류:', e);
  } finally {
    loading.value = false;
  }
};

const reloadSavedReport = async (text = '보고서 미리보기 불러오는 중...') => {
  if (!ccCode.value) return;

  loading.value = true;
  loadingText.value = text;
  error.value = '';
  resetPdf();

  try {
    const res = await crawlApi.getWageDiagnosisReport(ccCode.value);
    const base64 = res.data?.pdf;
    if (!base64) {
      throw new Error(res.data?.message || '보고서 데이터가 없습니다.');
    }
    applyPdfBase64(base64, res.data?.fileName, 'v1');
    await loadReportV2Pdf();
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `보고서 미리보기 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('저장 보고서 조회 오류:', e);
  } finally {
    loading.value = false;
  }
};

const loadSavedReport = async () => {
  await reloadSavedReport();
};

const onNonTaxLunchChange = async () => {
  if (!ccCode.value || !hasSaved.value) return;

  loading.value = true;
  loadingText.value = '설정 저장 및 보고서 재생성 중...';
  error.value = '';

  try {
    const res = await crawlApi.updateWageDiagnosisSettings(ccCode.value.trim(), {
      nonTaxLunch: nonTaxLunch.value,
    });
    if (!res.data?.success) {
      throw new Error(res.data?.message || '설정 저장에 실패했습니다.');
    }
    await reloadSavedReport('보고서 재생성 중...');
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `비과세 중식대 설정 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('비과세 중식대 설정 오류:', e);
    loading.value = false;
  }
};

const onFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  if (hasSaved.value && !confirm('기존 임금진단 데이터가 덮어쓰기 됩니다. 계속하시겠습니까?')) {
    if (fileRef.value) fileRef.value.value = '';
    return;
  }

  error.value = '';
  fileName.value = file.name;

  try {
    const buffer = await file.arrayBuffer();
    rows.value = parseExcel(buffer);
    if (rows.value.length === 0) {
      error.value = '엑셀에서 유효한 데이터를 찾을 수 없습니다.';
      return;
    }
    await uploadAndReport();
  } catch (e: any) {
    error.value = `엑셀 처리 실패: ${e.message || '파일을 읽을 수 없습니다.'}`;
    console.error('엑셀 파싱 오류:', e);
  } finally {
    if (fileRef.value) fileRef.value.value = '';
  }
};

const downloadTemplate = async () => {
  try {
    await crawlApi.downloadWageDiagnosisTemplate();
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '다운로드 실패';
    alert(`엑셀 양식 다운로드 실패: ${msg}`);
    console.error('양식 다운로드 오류:', e);
  }
};

const downloadPdf = () => {
  const blob = activePdfBlob.value;
  if (!blob) return;
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  link.href = url;
  link.download = activePdfFileName.value
    || (reportVersion.value === 'v1' ? buildPdfFileName() : buildPdfFileNameV2());
  link.click();
  window.URL.revokeObjectURL(url);
};

const confirmDelete = async () => {
  if (!ccCode.value) {
    error.value = '시설코드가 없습니다.';
    showDeleteConfirm.value = false;
    return;
  }

  deleting.value = true;
  error.value = '';

  try {
    const res = await crawlApi.deleteWageDiagnosis(ccCode.value.trim());
    if (!res.data?.success) {
      throw new Error(res.data?.message || '삭제에 실패했습니다.');
    }

    resetPdf();
    reportVersion.value = 'v1';
    rows.value = [];
    fileName.value = '';
    savedInfo.value = {
      ccNumber: ccCode.value,
      name: name.value,
      biz: biz.value,
      wageDiagCount: 0,
    };
    showDeleteConfirm.value = false;
    emit('deleted', ccCode.value);
  } catch (e: any) {
    const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
    const status = e.response?.status || '';
    error.value = `임금진단 삭제 실패: ${msg}${status ? ` (${status})` : ''}`;
    console.error('임금진단 삭제 오류:', e);
  } finally {
    deleting.value = false;
  }
};

watch(open, async (v) => {
  if (v) {
    error.value = '';
    if (!ym.value) initYm();
    await initFromFacility();
  } else {
    resetPdf();
    rows.value = [];
    fileName.value = '';
    error.value = '';
    ccCode.value = '';
    name.value = '';
    biz.value = '';
    savedInfo.value = null;
    reportVersion.value = 'v1';
    showDeleteConfirm.value = false;
    deleting.value = false;
  }
});

onUnmounted(() => {
  resetPdf();
});
</script>

<style lang="scss">
@import '@/assets/styles/components/wage-simple-diagnosis';
</style>
